import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, LockKeyholeIcon } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import api from "../../lib/api";
import toast from "react-hot-toast";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await api.post("/auth/forgot-password", { email });
      setIsSubmitted(true);
      toast.success("Password reset instructions sent to your email");
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 404) {
        toast.error("This email is not registered in our system.");
      } else {
        toast.error(error.response?.data?.message || "Failed to send reset email");
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="before:contents-[''] relative flex min-h-screen items-center justify-center bg-navy-900 bg-opacity-90 py-20 before:absolute before:inset-0 before:z-[-1] before:bg-login-screen before:bg-cover before:bg-center before:bg-no-repeat">
        <div className="w-full max-w-md px-4">
          {/* Logo and Header */}
          <div className="px-4 mb-12 text-center fade-in">
            {/* Logo */}
            <img src="/images/logo-white.svg" alt="Minesure Logo" className="w-full" />
          </div>

          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20">
            <Mail className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-center text-white mb-7">Check Your Email</h1>
          <p className="mb-3 text-sm font-normal text-center uppercase transition-colors text-gray-0">
            We've sent password reset instructions to{" "}
            <strong className="normal-case">{email}</strong>
          </p>
          <p className="flex items-center justify-center text-sm font-normal text-center text-gray-400 uppercase transition-colors mb-9">
            Didn't receive the email? Check your spam folder or try again.
          </p>

          <Link
            to="/login"
            className="flex items-center justify-center w-full h-16 text-sm font-normal uppercase bg-red-500 shadow-lg rounded-xl text-gray-0 hover:shadow-xl"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="before:contents-[''] relative flex min-h-screen items-center justify-center bg-navy-900 bg-opacity-90 py-20 before:absolute before:inset-0 before:z-[-1] before:bg-login-screen before:bg-cover before:bg-center before:bg-no-repeat">
      <div className="w-full max-w-md px-4">
        {/* Logo and Header */}
        <div className="px-4 mb-12 text-center fade-in">
          {/* Logo */}
          <img src="/images/logo-white.svg" alt="Minesure Logo" className="w-full" />
        </div>
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500/20">
          <LockKeyholeIcon className="w-8 h-8 text-blue-500" />
        </div>
        <h1 className="text-3xl font-bold text-center text-white mb-7">Forgot Password</h1>
        <p className="flex items-center justify-center text-sm font-normal text-center uppercase transition-colors mb-9 text-gray-0">
          Enter your email address and we'll send you a link to reset your password.
        </p>

        {/* Forgot Password Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Field */}
          <div className="relative w-full h-16">
            <label
              htmlFor="email"
              className="absolute left-4 top-2.5 z-10 m-0 block text-xxs font-normal uppercase leading-3 text-navy-900"
            >
              Email Address:
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="absolute inset-0 h-auto px-4 pt-6 border-0 text-color-gray-0 rounded-xl bg-gray-0 hover:outline-none focus:border-0 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
              required
            />
          </div>
          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full h-16 text-sm font-normal uppercase bg-red-500 shadow-lg rounded-xl text-gray-0 hover:shadow-xl"
          >
            {isLoading ? "Sending..." : "Send Reset Mail"}
          </Button>

          <div className="text-center">
            <Link
              to="/login"
              className="flex items-center justify-center text-sm font-normal uppercase transition-colors text-gray-0 hover:text-red-600"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
